// ── Shared TypeScript types ───────────────────────────────────────────────

import { localErrors, FALLBACK_INPUTS, coerceCalcInputs } from "@/lib/validators";
import { applyOnBlur, applyOnBlurWithNote, stripOverride, type BlurResult } from "@/lib/autocorrect";
import { GANTT_LIMITS, MAX_SHIFT_PLUS_OT_H, hardLimitError } from "@/lib/limits";
import { calculate } from "@/lib/api";
import type { CalcInputs } from "@/types/calc";

export interface GanttInputs {
  // Supply & shift (Gantt-own)
  mFlow: string;
  tShift: string;
  startStockBatches: string;
  goalT: string;
  // Freezer sizing (linked to Calculator)
  nFreezers: string;
  nStations: string;
  lStation: string;
  dStation: string;
  tStation: string;
  nDividers: string;
  // Product (linked)
  rhoProduct: string;
  // Cycle times (linked to Calculator result). closingSec / openingSec are
  // read-only on the Gantt — always taken from the Calculator result.
  closingSec: string;
  loadingSec: string;
  freezeOverride: string;   // "" = AUTO (computed live), non-empty = MAN override (min)
  defrostOverride: string;  // "" = AUTO (computed live), non-empty = MAN override (min)
  openingSec: string;
  unloadingSec: string;
  waitingMin: string;
  // Crews (Gantt-own)
  nCrews: string;
  requireFullCycle: boolean;
  overtimeH: string;        // max unloading-only overtime after the shift (h); "0" = off
  // Goal seek (Gantt-own)
  solveFor: "flow" | "crews" | "flow+crews";
}

export interface Segment {
  freezer: number;
  phase: string;
  start_min: number;
  end_min: number;
  reason?: string;
}

export interface DerivedResult {
  m_station_kg: number;
  m_batch_kg: number;
  t_load_h: number;
  t_freeze_h: number;
  t_service_h: number;
  t_cycle_h: number;
}

export interface SimMainResult {
  batches: number;
  mass_kg: number;
  n_crews: number;
  crew_util: number;
  first_load_min: number;
  flow_limited_fh: number;
  crew_limited_fh: number;
  segments: Segment[];
  buffer_t_min: number[];
  buffer_kg: number[];
  cum_in_kg: number[];
  cum_out_kg: number[];
  frozen_kg: number[];
  derived: DerivedResult;
  // Unloading-overtime policy + product-wait metrics
  t_shift_h?: number;
  overtime_used_h?: number;     // last unload end − shift end (h), ≥ 0
  wait_max_min?: number;        // worst-case min from product arrival to freezer closed
  wait_avg_min?: number;
  carry_over_kg?: number;       // raw product left unfrozen at the end
  // Hours the last unload runs past the overtime cap (mostly while "Require
  // full cycle" is off; also crews still unloading at the end), ≥ 0. Older
  // backends omit it.
  overtime_overrun_h?: number;
}

/** `result` of POST /api/batches/solve_goal (only the fields the UI reads). */
export interface SolveGoalResult {
  [key: string]: unknown;
  overtime_overrun_h?: number;  // h past the overtime cap, ≥ 0 (see SimMainResult)
}

export interface CrewSweepRow {
  n_crews: number;
  batches: number;
  mass_kg: number;
  crew_util: number;
}

export interface FlowSweepRow {
  m_flow_kg_h: number;
  batches: number;
  mass_kg: number;
}

export interface FullResult {
  main: SimMainResult;
  crew_sweep: CrewSweepRow[];
  flow_sweep: FlowSweepRow[];
  rec_crews: number;
}

// Plant-optimiser goal the customer can favour.
export type OptObjective = "cost" | "freezers" | "fresh" | "flow";

// "Lowest overall cost" stays disabled until the cost weights are researched.
// Enable together with COST_MODEL_ENABLED in backend/batches_per_day.py once
// plant_cost() / DEFAULT_COST_WEIGHTS hold real Jackstone estimates. While
// disabled, the backend breaks ties with a lean order (crews → freezers →
// stations → overtime → flow) instead of cost, and returns cost_score = null.
export const COST_GOAL_ENABLED = false;
export const DEFAULT_OPT_OBJECTIVE: OptObjective = COST_GOAL_ENABLED ? "cost" : "freezers";

const TIE_BREAK = COST_GOAL_ENABLED ? "lowest overall cost" : "the leanest plant (fewest crews, freezers, stations)";

export const OPT_OBJECTIVES: { key: OptObjective; label: string; hint: string; disabled?: boolean }[] = [
  {
    key: "cost", label: "Lowest overall cost", disabled: !COST_GOAL_ENABLED,
    hint: COST_GOAL_ENABLED
      ? "Cheapest plant by the cost weights — freezers, stations, crews and overtime together."
      : "Coming soon — cost estimates are still being researched.",
  },
  { key: "freezers", label: "Fewest freezers",  hint: `Smallest number of freezers; ties go to ${TIE_BREAK}.` },
  { key: "fresh",    label: "Freshest product", hint: `Shortest wait from product arriving to the freezer closing (smaller batches), then ${TIE_BREAK}.` },
  { key: "flow",     label: "Lowest raw flow",  hint: `Least raw product flow needed (flows within 3 % count as equal), then ${TIE_BREAK}.` },
];

/** One design in the optimiser's trade-off table (all metrics at the nominal
 *  freeze time — i.e. exactly what the Gantt shows once it is applied). */
export interface OptimizeOption {
  n_freezers: number;
  n_stations: number;           // per freezer
  n_stations_total: number;     // n_freezers × n_stations
  n_crews: number;
  m_flow_kg_h: number;
  crew_util: number;            // 0..1
  achieved_t: number;
  headroom_pct: number;         // extra freeze time (%) it tolerates and still meets target
  wait_max_min: number;         // worst-case min from product arrival to freezer closed
  wait_avg_min: number;
  overtime_used_h: number;
  cost_score: number | null;    // plant_cost() — relative, lower is better; null while the cost goal is disabled
  best_for: OptObjective[];     // goals this design wins
  is_winner: boolean;           // the design chosen for the selected goal
}

export interface OptimizeResult {
  feasible: boolean;
  mode: string;                 // "smooth" | "best-effort"
  m_flow_kg_h: number;
  n_crews: number;
  n_freezers: number;
  n_stations: number;
  n_stations_total: number;
  crew_util: number;            // 0..1
  achieved_kg: number;
  achieved_t: number;
  target_t: number;
  shortfall_t: number;
  headroom_pct: number;
  wait_max_min: number;
  wait_avg_min: number;
  overtime_used_h: number;
  cost_score: number | null;                           // null while the cost goal is disabled
  cost_weights: Record<CostWeightKey, number> | null;  // effective weights used; null while disabled
  flow_estimate_kg_h: number;
  sims_run: number;
  truncated: boolean;           // search hit its time budget (result may not be optimal)
  objective: OptObjective;      // after legacy/unknown/disabled mapping
  options: OptimizeOption[];    // trade-off table (by cost, or leanest-first while cost is disabled); winner included
  message: string;
}

// Cost weights (station-equivalents: 1 = one station incl. its refrigeration
// share). Defaults live in the backend (batches_per_day.DEFAULT_COST_WEIGHTS);
// the UI only sends the ones the user overrides.
export type CostWeightKey = "freezer" | "station" | "crew" | "overtime_h";
export type CostWeightOverrides = Partial<Record<CostWeightKey, string>>;  // "" = default

export const COST_WEIGHT_FIELDS: { key: CostWeightKey; label: string; hint: string }[] = [
  { key: "freezer",    label: "Per freezer",           hint: "Frame, hydraulic pack, controls and installation" },
  { key: "station",    label: "Per station",           hint: "Plates, hoses and refrigeration share — the unit" },
  { key: "crew",       label: "Per crew",              hint: "Labour over the payback period" },
  { key: "overtime_h", label: "Per overtime crew-hour", hint: "Unloading overtime, each shift" },
];

/** Request body for POST /api/batches/optimize. */
export interface OptimizeRequestBody {
  params: ReturnType<typeof toPayload>;
  objective: OptObjective;
  flow_margin_pct: number;
  freeze_margin_pct: number;
  max_crews: number;                                   // 1..4
  cost_weights: Partial<Record<CostWeightKey, number>> | null;
}

// ── localStorage keys ─────────────────────────────────────────────────────

export const GANTT_KEY  = "jackstone-gantt-inputs-v1";
export const CALC_KEY   = "jackstone-vpf-inputs-v1";
export const RESULT_KEY = "jackstone-vpf-result-v1";

// Fields the Gantt owns independently (not overwritten by Calculator sync).
// tShift is NOT here: the shift length is the Calculator's cyclesWindowHrs
// (single source of truth) — pulled on mount, written back via LINKED_TO_CALC.
// A stale tShift left under GANTT_KEY by older builds is simply ignored.
export const GANTT_OWN_KEYS: (keyof GanttInputs)[] = [
  "mFlow", "startStockBatches", "goalT",
  "nCrews", "requireFullCycle", "overtimeH", "solveFor",
];

// ── Shared validation / autocorrect (parity with the Calculator) ───────────
//
// Every linked Gantt field maps to its Calculator field so BOTH pages run the
// *exact same* validation (lib/validators) and autocorrect (lib/autocorrect)
// rules. Change a field here → it behaves identically on the Calculator, and
// the value is mirrored through localStorage. The two pages act as one.
export const LINKED_TO_CALC: Partial<Record<keyof GanttInputs, keyof CalcInputs>> = {
  nFreezers:    "nFreezers",
  nStations:    "nStations",
  lStation:     "lStation",
  dStation:     "dStation",
  tStation:     "tStation",
  nDividers:    "nDividers",
  rhoProduct:   "density",
  loadingSec:   "loadingTime",
  unloadingSec: "unloadingTime",
  waitingMin:   "waitMisc",
  tShift:       "cyclesWindowHrs",
};

/**
 * Apply the Calculator's on-blur autocorrect rule for a linked field
 * (e.g. Station length 815 → 820, thickness 75 → 76, density clamp 700–1200).
 * Gantt-own fields without a Calculator rule are returned unchanged.
 */
export function autocorrectLinked(key: keyof GanttInputs, value: string): string {
  const calcKey = LINKED_TO_CALC[key];
  return calcKey ? applyOnBlur(calcKey, value) : value;
}

/** autocorrectLinked plus a note explaining any change ("815 → 820 (standard size)"). */
export function autocorrectLinkedWithNote(key: keyof GanttInputs, value: string): BlurResult {
  const calcKey = LINKED_TO_CALC[key];
  return calcKey ? applyOnBlurWithNote(calcKey, value) : { value };
}

/**
 * Validate the linked Gantt fields with the SAME rules the Calculator uses
 * (lib/validators → localErrors): blank/NaN checks, hard limits (lib/limits),
 * dividers 0–4 integer and the plate stack-closed constraint. Returns a map
 * keyed by Gantt field.
 */
export function ganttLinkedErrors(
  inputs: GanttInputs,
): Partial<Record<keyof GanttInputs, string>> {
  // Build a CalcInputs-shaped object from the linked values (other fields keep
  // their valid fallback defaults so they never produce spurious errors).
  const shaped: CalcInputs = { ...FALLBACK_INPUTS, refrigerantType: "Primary" };
  (Object.keys(LINKED_TO_CALC) as (keyof GanttInputs)[]).forEach((gk) => {
    (shaped as any)[LINKED_TO_CALC[gk]!] = inputs[gk];
  });

  const calcErrs = localErrors(shaped);

  const out: Partial<Record<keyof GanttInputs, string>> = {};
  (Object.keys(LINKED_TO_CALC) as (keyof GanttInputs)[]).forEach((gk) => {
    const ck = LINKED_TO_CALC[gk]!;
    if (calcErrs[ck]) (out as any)[gk] = calcErrs[ck];
  });
  return out;
}

// Gantt-own numeric fields checked against GANTT_LIMITS (blank = error).
const GANTT_OWN_NUMERIC: (keyof GanttInputs)[] = [
  "mFlow", "startStockBatches", "goalT", "closingSec", "openingSec", "nCrews", "overtimeH",
];
// MAN overrides — blank = AUTO; anything else (incl. "1.2.3") is validated.
const GANTT_OVERRIDES: (keyof GanttInputs)[] = ["freezeOverride", "defrostOverride"];

/**
 * Every Gantt field error: the linked fields (Calculator rules, above), the
 * Gantt-own fields and MAN overrides (hard limits from lib/limits — "!" never
 * bypasses them) and the shift + overtime ≤ 24 h rule. Any entry blocks the
 * simulation, exactly as localErrors blocks the Calculator.
 */
export function ganttErrors(
  inputs: GanttInputs,
): Partial<Record<keyof GanttInputs, string>> {
  const out = ganttLinkedErrors(inputs);
  const blankOrNaN = (v: string) => {
    const c = (v || "").replace(/!/g, "").trim();
    return c === "" || Number.isNaN(Number(c));
  };
  const check = (k: keyof GanttInputs) => {
    const v = String(inputs[k] ?? "");
    if (blankOrNaN(v)) { out[k] = "Enter a number"; return; }
    const hard = hardLimitError(GANTT_LIMITS[k], v);
    if (hard) out[k] = hard;
  };

  GANTT_OWN_NUMERIC.forEach(check);
  GANTT_OVERRIDES.forEach((k) => {
    if (String(inputs[k] ?? "").trim() !== "") check(k);
  });

  // Shift + unloading overtime must fit in one day
  if (!out.tShift && !out.overtimeH) {
    const total = n(inputs.tShift.replace(/!/g, "")) + n(inputs.overtimeH.replace(/!/g, ""));
    if (total > MAX_SHIFT_PLUS_OT_H) {
      const msg = `Shift + overtime must be ≤ ${MAX_SHIFT_PLUS_OT_H} h (now ${+total.toFixed(2)} h)`;
      out.tShift = msg;
      out.overtimeH = msg;
    }
  }
  return out;
}

export const GANTT_DEFAULTS: GanttInputs = {
  mFlow: "1600",
  tShift: "16",
  startStockBatches: "1",
  goalT: "20",
  nFreezers: "4",
  nStations: "36",
  lStation: "810",
  dStation: "600",
  tStation: "76",
  nDividers: "1",
  rhoProduct: "890",
  closingSec: "30",
  loadingSec: "15",
  freezeOverride: "",
  defrostOverride: "",
  openingSec: "40",
  unloadingSec: "15",
  waitingMin: "5",
  nCrews: "1",
  requireFullCycle: true,
  overtimeH: "0",
  solveFor: "flow",
};

// ── State builders ────────────────────────────────────────────────────────

/** Called on every Gantt mount — always syncs linked fields from Calculator. */
export function buildGanttInputs(): GanttInputs {
  let own: GanttInputs = { ...GANTT_DEFAULTS };

  // Restore Gantt-own fields from previous session — type-checked per key so
  // a corrupt / hand-edited entry can't crash .trim() further down.
  try {
    const raw = localStorage.getItem(GANTT_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    if (saved && typeof saved === "object" && !Array.isArray(saved)) {
      GANTT_OWN_KEYS.forEach(k => {
        const v = saved[k];
        if (k === "requireFullCycle") {
          if (typeof v === "boolean") own.requireFullCycle = v;
        } else if (k === "solveFor") {
          if (v === "flow" || v === "crews" || v === "flow+crews") own.solveFor = v;
        } else if (typeof v === "string" || (typeof v === "number" && isFinite(v))) {
          (own as any)[k] = String(v);
        }
      });
    }
  } catch {/* ignore */}

  // Always pull freezer sizing from Calculator inputs
  try {
    const calcRaw = localStorage.getItem(CALC_KEY);
    if (calcRaw) {
      const c = coerceCalcInputs(JSON.parse(calcRaw));
      if (c.nFreezers    != null) own.nFreezers   = String(c.nFreezers);
      if (c.nStations    != null) own.nStations   = String(c.nStations);
      if (c.lStation     != null) own.lStation    = String(c.lStation);
      if (c.dStation     != null) own.dStation    = String(c.dStation);
      if (c.tStation     != null) own.tStation    = String(c.tStation);
      if (c.nDividers    != null) own.nDividers   = String(c.nDividers);
      if (c.density      != null) own.rhoProduct  = String(c.density);
      if (c.loadingTime  != null) own.loadingSec  = String(c.loadingTime);
      if (c.unloadingTime != null) own.unloadingSec = String(c.unloadingTime);
      if (c.waitMisc     != null) own.waitingMin  = String(c.waitMisc);
      if (c.cyclesWindowHrs != null) own.tShift   = String(c.cyclesWindowHrs);
      // AUTO/MAN state is shared via the Calculator's override fields.
      // "" / null = AUTO (freeze + defrost computed live); non-empty = MAN.
      if (c.overrideFreezeTime != null) own.freezeOverride  = String(c.overrideFreezeTime);
      if (c.overrideDefrostMin != null) own.defrostOverride = String(c.overrideDefrostMin);
    }
  } catch {/* ignore */}

  // Pull the fixed cycle constants (close / open) from the Calculator result.
  // Freeze time + defrosting are NOT copied here — they are recomputed live so
  // they always stay in sync with the current settings (see buildMergedCalcInputs).
  try {
    const resRaw = localStorage.getItem(RESULT_KEY);
    if (resRaw) {
      const res = JSON.parse(resRaw);
      if (res.closingTime != null) own.closingSec = String(Math.round(res.closingTime));
      if (res.openingTime != null) own.openingSec = String(Math.round(res.openingTime));
    }
  } catch {/* ignore */}

  return own;
}

/** Only persist Gantt-own fields; linked fields always re-sync from Calculator. */
export function saveGanttInputs(inputs: GanttInputs) {
  const own: Partial<GanttInputs> = {};
  GANTT_OWN_KEYS.forEach(k => { (own as any)[k] = inputs[k]; });
  try { localStorage.setItem(GANTT_KEY, JSON.stringify(own)); } catch {/* ignore */}
}

/**
 * Write every linked field back to the Calculator's localStorage key so
 * navigating Calculator → Gantt → Calculator sees consistent values in both
 * directions. This is what makes the two pages "act as one".
 *
 * Mapping (Gantt → CalcInputs):
 *   nFreezers / nStations / lStation / dStation / tStation / nDividers
 *   rhoProduct  → density
 *   loadingSec  → loadingTime  (sec/stn)
 *   unloadingSec → unloadingTime (sec/stn)
 *   waitingMin  → waitMisc  (min)
 *   tShift      → cyclesWindowHrs (h)
 *   freezeOverride  → overrideFreezeTime  ("" = AUTO, shared with Calculator)
 *   defrostOverride → overrideDefrostMin  ("" = AUTO, shared with Calculator)
 */
export function syncLinkedFieldsToCalc(inputs: GanttInputs) {
  try {
    const raw = localStorage.getItem(CALC_KEY);
    const calc = raw ? JSON.parse(raw) : {};

    calc.nFreezers       = inputs.nFreezers;
    calc.nStations       = inputs.nStations;
    calc.lStation        = inputs.lStation;
    calc.dStation        = inputs.dStation;
    calc.tStation        = inputs.tStation;
    calc.nDividers       = inputs.nDividers;
    calc.density         = inputs.rhoProduct;
    calc.loadingTime     = inputs.loadingSec;
    calc.unloadingTime   = inputs.unloadingSec;
    calc.waitMisc        = inputs.waitingMin;
    calc.cyclesWindowHrs = inputs.tShift;
    // Shared AUTO/MAN state — "" keeps the Calculator in AUTO (computed) mode.
    calc.overrideFreezeTime = inputs.freezeOverride;
    calc.overrideDefrostMin = inputs.defrostOverride;

    localStorage.setItem(CALC_KEY, JSON.stringify(calc));
  } catch {/* ignore */}
}

/**
 * Build a full CalcInputs object for a LIVE freeze-time calculation.
 *
 * It starts from the Calculator's persisted inputs (product, temps, refrigerant
 * — which the Gantt does not edit) and overlays the Gantt's current linked
 * fields. overrideFreezeTime / overrideDefrostMin are cleared so the backend
 * returns the pure *computed* values (estFreezingTimeCalc / defrosting), which
 * the Gantt then uses for AUTO mode. This is what keeps the freeze time live
 * and identical to the Calculator.
 */
export function buildMergedCalcInputs(g: GanttInputs): CalcInputs {
  let base: Partial<CalcInputs> = {};
  try {
    const raw = localStorage.getItem(CALC_KEY);
    if (raw) base = coerceCalcInputs(JSON.parse(raw));
  } catch {/* ignore */}

  return {
    ...FALLBACK_INPUTS,
    ...base,
    // current Gantt linked values take precedence
    nFreezers:   g.nFreezers,
    nStations:   g.nStations,
    lStation:    g.lStation,
    dStation:    g.dStation,
    tStation:    g.tStation,
    nDividers:   g.nDividers,
    density:     g.rhoProduct,
    loadingTime: g.loadingSec,
    unloadingTime: g.unloadingSec,
    waitMisc:    g.waitingMin,
    cyclesWindowHrs: g.tShift,
    // force pure computed values
    overrideFreezeTime: "",
    overrideDefrostMin: "",
  } as CalcInputs;
}

// ── Number / format helpers ───────────────────────────────────────────────

export const n = (v: string, fallback = 0) => {
  const x = parseFloat(v);
  return isFinite(x) ? x : fallback;
};

/** Model code from the sizing inputs ("!" override markers stripped). */
export const ganttModelCode = (inp: GanttInputs) =>
  `VPF ${stripOverride(inp.nStations)}-${stripOverride(inp.lStation)}-${stripOverride(inp.dStation)}`
  + `-${stripOverride(inp.tStation)}-${stripOverride(inp.nDividers)}D`;

export const fmt = (x: number | null | undefined, d = 0) =>
  x == null || !isFinite(x) ? "—" : x.toLocaleString("en-GB", {
    minimumFractionDigits: d, maximumFractionDigits: d,
  });

export const fmtKg = (x: number) =>
  x >= 1000 ? (x / 1000).toFixed(1) + " t" : Math.round(x) + " kg";

// ── Schedule axis + unloading overtime (shared by Gantt + Scenarios) ────────

// Overtime (after the shift end) — band, shift-end marker + legend swatch.
export const OT_INK    = "#4B4B9D";
export const OT_BAND   = "repeating-linear-gradient(-45deg, rgba(75,75,157,.22) 0 2px, rgba(75,75,157,.07) 2px 7px)";
export const OT_SWATCH = "repeating-linear-gradient(-45deg, rgba(75,75,157,.45) 0 2px, rgba(75,75,157,.12) 2px 5px)";

export interface HourTick { h: number; label: string; kind: "reg" | "shift" | "end" }

/** Axis hour label with an "h" suffix, as on the buffer chart: 16 → "16h", 17.94 → "17.9h". */
const fmtHour = (h: number) =>
  `${Math.abs(h - Math.round(h)) < 0.05 ? Math.round(h) : h.toFixed(1)}h`;

/**
 * Hour ticks for the schedule axis ("0h", "2h", …): every `step` h (picked
 * from the TOTAL span) from 0 to the end. With overtime, the shift end and the
 * overtime end get their own labels and regular ticks within half a step of
 * them are dropped so labels never collide — the shift end wins a clash, as it
 * is the reference the dashed marker sits on. Without overtime it is the
 * original set.
 */
export function hourTicks(shiftH: number, spanH: number, overtime: boolean): HourTick[] {
  const step = spanH <= 8 ? 1 : spanH <= 16 ? 2 : 3;
  const reg: HourTick[] = [];
  for (let h = 0; h <= spanH + 1e-9; h += step) reg.push({ h, label: fmtHour(h), kind: "reg" });
  if (!overtime) return reg;
  const kept: HourTick[] = [];
  const candidates: HourTick[] = [
    { h: shiftH, label: fmtHour(shiftH), kind: "shift" },
    { h: spanH,  label: fmtHour(spanH),  kind: "end" },
    ...reg,
  ];
  candidates.forEach(t => {
    if (kept.every(k => Math.abs(k.h - t.h) >= step / 2 - 1e-9)) kept.push(t);
  });
  return kept.sort((a, b) => a.h - b.h);
}

// ── Simulation payload builder (shared by Gantt + PDF export) ──────────────

/** Convert Gantt inputs + resolved freeze/defrost minutes into the backend
 *  BatchParams payload. */
export function toPayload(inp: GanttInputs, freezingMin: number, defrostingMin: number) {
  return {
    m_flow_kg_h:               n(inp.mFlow, 1600),
    t_shift_h:                 n(inp.tShift, 16),
    start_stock_batches:       n(inp.startStockBatches, 0),
    n_freezers:                Math.max(1, Math.round(n(inp.nFreezers, 4))),
    n_stations:                Math.max(1, Math.round(n(inp.nStations, 36))),
    n_dividers:                Math.max(0, Math.round(n(inp.nDividers, 1))),
    l_station_mm:              n(inp.lStation, 810),
    d_station_mm:              n(inp.dStation, 600),
    t_station_mm:              n(inp.tStation, 76),
    rho_product:               n(inp.rhoProduct, 890),
    closing_sec:               n(inp.closingSec, 30),
    loading_sec_per_station:   n(inp.loadingSec, 15),
    freezing_min:              freezingMin,
    defrosting_min:            defrostingMin,
    opening_sec:               n(inp.openingSec, 40),
    unloading_sec_per_station: n(inp.unloadingSec, 15),
    waiting_min:               n(inp.waitingMin, 5),
    n_crews:                   Math.max(1, Math.round(n(inp.nCrews, 1))),
    require_full_cycle:        inp.requireFullCycle,
    overtime_h:                Math.max(0, n(inp.overtimeH, 0)),
    goal_t:                    n(inp.goalT, 0),
  };
}

/**
 * Build the PDF-report request body from the CURRENT (live) Gantt state.
 * Reads the persisted Gantt inputs, resolves the effective freeze / defrost
 * times exactly as the Gantt page does (MAN override, else the live computed
 * value from the calc engine), and returns the payload + derived metadata.
 */
export async function buildGanttExportPayload(): Promise<{
  params: ReturnType<typeof toPayload>;
  model_code: string;
  n_freezers: number;
}> {
  const inp = buildGanttInputs();

  const fo = inp.freezeOverride.trim();
  const dofo = inp.defrostOverride.trim();
  const foValid = fo !== "" && isFinite(Number(fo));
  const dofoValid = dofo !== "" && isFinite(Number(dofo));

  // AUTO values come from the calc engine — no hard-coded fallback, so a
  // report is never built on a made-up freeze time.
  let autoFreeze: number | null = null;
  let autoDefrost: number | null = null;
  if (!foValid || !dofoValid) {
    const resp = await calculate(buildMergedCalcInputs(inp));
    if (resp.ok && resp.result) {
      if (resp.result.estFreezingTimeCalc != null) autoFreeze = Math.round(resp.result.estFreezingTimeCalc);
      if (resp.result.defrosting != null) autoDefrost = resp.result.defrosting;
    }
  }

  const freeze = foValid ? Number(fo) : autoFreeze;
  const defrost = dofoValid ? Number(dofo) : autoDefrost;
  if (freeze == null || defrost == null) {
    throw new Error("Could not compute the freeze / defrost time — check the Calculator inputs.");
  }
  const params = toPayload(inp, freeze, defrost);
  const model_code = ganttModelCode(inp);
  return { params, model_code, n_freezers: params.n_freezers };
}
